import { Controller, Control, FieldErrors } from 'react-hook-form';
import { LoanFormData } from '@/schemas/loanSchema';
import { Label } from '@/components/ui/label';
import USFormattedNumberInput from '@/components/ui/USFormattedNumberInput';
import { DatePicker } from '@/components/ui/date-picker';

interface Props {
  control: Control<LoanFormData>;
  errors: FieldErrors<LoanFormData>;
}

export const PropertyMetricsSection = ({ control }: Props) => (
  <div className="p-8 mb-8" data-test-id="property-metrics-container">
    <h3 className="section-title" data-test-id="property-metrics-section">
      Property Financials & Leverage
    </h3>
    <div className="grid-3" data-test-id="property-metrics-grid-1">
      <div data-test-id="noi-field">
        <Label
          className="form-label loanform-label"
          htmlFor="property_metrics.noi"
          data-test-id="noi-label"
        >
          Net Operating Income
        </Label>
        <Controller
          name="property_metrics.noi"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="currency"
              decimals={2}
              step={1000}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="noi-input"
            />
          )}
        />
      </div>
      <div data-test-id="property-appraisal-value-field">
        <Label
          className="form-label loanform-label"
          htmlFor="property_metrics.property_appraisal_value"
          data-test-id="property-appraisal-value-label"
        >
          Appraisal Value
        </Label>
        <Controller
          name="property_metrics.property_appraisal_value"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="currency"
              decimals={0}
              step={100000}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="property-appraisal-value-input"
            />
          )}
        />
      </div>
      <div data-test-id="property-appraisal-value-date-field">
        <Label
          className="form-label loanform-label"
          htmlFor="property_metrics.property_appraisal_value_date"
          data-test-id="property-appraisal-value-date-label"
        >
          Appraisal Value Date
        </Label>
        <Controller
          name="property_metrics.property_appraisal_value_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={field.value ? (typeof field.value === 'string' ? field.value : new Date(field.value).toISOString().split('T')[0]) : ''}
              onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
              data-test-id="property-appraisal-value-date-input"
            />
          )}
        />
      </div>
    </div>

    <div className="grid-2 mt-6" data-test-id="property-metrics-grid-2">
      <div data-test-id="dscr-field">
        <Label
          className="form-label loanform-label"
          htmlFor="property_metrics.dscr_current"
          data-test-id="dscr-label"
        >
          DSCR*
        </Label>
        <Controller
          name="property_metrics.dscr_current"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="number"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="dscr-input"
            />
          )}
        />
      </div>
      <div data-test-id="ltv-field">
        <Label
          className="form-label loanform-label"
          htmlFor="property_metrics.ltv_current"
          data-test-id="ltv-label"
        >
          LTV*
        </Label>
        <Controller
          name="property_metrics.ltv_current"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="ltv-input"
            />
          )}
        />
      </div>
    </div>
  </div>
);
